import * as React from 'react';

import { refreshDataSourceTables } from 'src/actions/data-sources/action-creators/refreshDataSourceTables';
import { dataSourcesStardogRequestDispatchers } from 'src/actions/data-sources/actionCreators';
import { DataSourcesActionType } from 'src/actions/data-sources/actionTypes';
import { connectionSelector } from 'src/selectors/connection';
import { useCanWriteSpecificDataSource } from 'src/utils/hooks/useDesignerPermissions';
import { useIsMounted } from 'src/utils/hooks/useIsMounted';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';

export const useRefreshDataSourceTypeMetadata = (
  dataSourceId: string | undefined,
  onRefreshComplete: () => void
) => {
  const dispatch = useReduxDispatch();
  const connection = useReduxSelector(connectionSelector);
  const canWriteSpecificDataSource = useCanWriteSpecificDataSource();
  const isMounted = useIsMounted();

  const [isRefreshingMetadata, setIsRefreshingMetadata] = React.useState(false);

  const isMetadataRefreshable = Boolean(
    dataSourceId && connection && canWriteSpecificDataSource(dataSourceId)
  );

  const refreshMetadata = React.useCallback(async () => {
    if (!isMetadataRefreshable || !dataSourceId || !connection) {
      return;
    }
    setIsRefreshingMetadata(true);
    const result =
      await dataSourcesStardogRequestDispatchers.refreshDataSourceTypeMetadata(
        dataSourceId,
        connection
      );
    if (!isMounted.current) {
      return;
    }
    if (
      result.type === DataSourcesActionType.REFRESH_DATA_SOURCE_METADATA_SUCCESS
    ) {
      await dispatch(refreshDataSourceTables(dataSourceId));
      if (!isMounted.current) {
        return;
      }
      onRefreshComplete();
    }
    setIsRefreshingMetadata(false);
  }, [
    isMetadataRefreshable,
    dataSourceId,
    connection,
    dispatch,
    isMounted,
    onRefreshComplete,
  ]);

  return { isRefreshingMetadata, isMetadataRefreshable, refreshMetadata };
};
